/**
 * Timeline Search Component
 * PROJ-024 - Fase 4: Search & Export
 *
 * Debounced search bar for timeline events
 */

import { useState, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, X } from 'lucide-react';

interface TimelineSearchProps {
  onSearchChange: (search: string) => void;
  placeholder?: string;
}

export function TimelineSearch({
  onSearchChange,
  placeholder = 'Buscar en el historial...'
}: TimelineSearchProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');

  // Debounce search term (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Notify parent when debounced term changes
  useEffect(() => {
    onSearchChange(debouncedTerm);
  }, [debouncedTerm, onSearchChange]);

  const handleClear = useCallback(() => {
    setSearchTerm('');
    setDebouncedTerm('');
  }, []);

  return (
    <div className="relative flex items-center gap-2">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9 pr-9"
        />
        {searchTerm && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      {debouncedTerm !== searchTerm && (
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          Buscando...
        </span>
      )}
    </div>
  );
}
